import ErrorBoundary from 'components/common/ErrorBoundary';
import WelcomeScreen from 'components/common/WelcomeScreen';
import { checkDomainAllowFetchDataConfig } from 'components/common/func';
import {
  fetchConfigOccupation,
  fetchConfigPurpose,
  fetchConfigPurposeOfBusiness,
  fetchConfigRelationship,
  fetchConfigRelationshipOfBusiness,
  fetchConfigSourcesOfWealth,
  fetchResources,
} from 'config';
import {
  HHMT_THEME,
  THEME_REFERENCE,
  THEME_STYLESHEET_REFERENCE,
} from 'constants';
import { useBuildDetector } from 'hooks/useBuildDetector';
import useDeviceId from 'hooks/useDeviceId';
import useFirebase from 'hooks/useFirebase';
import useLang from 'hooks/useLang';
import useLoadCSS from 'hooks/useLoadCSS';
import useLoadJS from 'hooks/useLoadJS';
import usePageYOffset from 'hooks/usePageYOffset';
import useRetriveNotificationsRealtimeDB from 'hooks/useRetriveNotificationsRealtimeDB';
import useToastify from 'hooks/useToastify';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Router from './router';

function App() {
  const { locate } = useLang();
  const { requestPushApiSubcribe } = useFirebase();
  const { renderToastContainer } = useToastify();

  const [isResourcesLoaded, setResourcesLoaded] = useState(false);
  const [pageYOffset, setPageYOffset] = useState(0);

  const { requestBuildDetectorApiSubcribe, newBuildDetected, refreshApp } =
    useBuildDetector();

  useLoadCSS(
    THEME_STYLESHEET_REFERENCE[
      THEME_REFERENCE[process.env.REACT_APP_APP_NAME || HHMT_THEME]
    ],
  );

  useLoadJS('https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.3.200/pdf.js');
  useLoadJS('https://cdnjs.com/libraries/pdf.js');

  useEffect(() => {
    fetchResources(() => setResourcesLoaded(true));
  }, []);

  useEffect(() => {
    if (!checkDomainAllowFetchDataConfig()) return;

    if (isResourcesLoaded && locate) {
      Promise.all([
        fetchConfigOccupation(locate),
        fetchConfigRelationship(locate),
        fetchConfigRelationshipOfBusiness(locate),
        fetchConfigPurpose(locate),
        fetchConfigPurposeOfBusiness(locate),
        fetchConfigSourcesOfWealth(locate),
      ]);

      requestPushApiSubcribe();

      requestBuildDetectorApiSubcribe();
    }
  }, [isResourcesLoaded, locate]);

  usePageYOffset(setPageYOffset);

  useRetriveNotificationsRealtimeDB();

  useDeviceId();

  useEffect(() => {
    if (!isResourcesLoaded) return;
    if (!newBuildDetected) return;
    refreshApp();
  }, [isResourcesLoaded, newBuildDetected]);

  if (!isResourcesLoaded)
    return (
      <WelcomeScreen
        pageYOffset={pageYOffset}
        loadingStyles={{ background: '#fff' }}
      />
    );
  return (
    <AppStyled id="application-root">
      <ErrorBoundary>
        <Router />
      </ErrorBoundary>
      {renderToastContainer()}
    </AppStyled>
  );
}

const AppStyled = styled.div`
  height: fit-content;
  width: 100%;

  @media screen and (min-width: 1280px) {
    background-repeat: no-repeat;
    background-color: var(--c-primary);
  }

  @media screen and (min-width: 1280px) {
    background-size: auto 92%;
  }

  @media screen and (min-width: 1440px) {
    background-size: auto 93%;
  }

  @media screen and (min-width: 1600px) {
    background-size: auto 93%;
  }

  @media screen and (min-width: 1680px) {
    background-size: auto 94%;
  }

  @media screen and (min-width: 1920px) {
    background-size: auto 94%;
  }

  @media screen and (min-width: 2560px) {
    background-size: auto 96%;
  }

  @media screen and (min-width: 3840px) {
    background-size: auto 97%;
  }

  @media screen and (min-width: 5120px) {
    background-size: cover;
  }
`;

export default App;
